import React, { useMemo } from "react";
import MathExpressionNew from "../common/mathjax"; // Adjust the import path as needed
import { useQuestionsStore } from "../../utils/questionStore";
import { vIcon, orangeVIcon, Loader } from "../../assets/images/index";
import "./exercise-question-list.scss"; // Add styles if needed
import { useLayoutContext } from "../../layouts/general-layout";
import { Link } from "@mui/material";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import { useExerciseStore } from "../../utils/exerciseStore";
import { useTranslation } from "react-i18next";

interface ExerciseQuestion {
  id: number;
  question: string;
  chart_image_data?: string | null;
  isCompleted?: boolean;
  isInProcess?: boolean; 
}

interface ExerciseQuestionListProps {
  exerciseQuestions: ExerciseQuestion[];
  hebrewLetters: string[];
  onSelectQuestion?: (question: ExerciseQuestion, hebrewLetter: string) => void; // Optional for view-only mode
  selectedQuestionId?: number | null; // Optional for view-only mode
  isViewOnly?: boolean; // New prop for view-only mode
  showChartLink?: boolean;
}

const ExerciseQuestionList: React.FC<ExerciseQuestionListProps> = ({
  exerciseQuestions,
  hebrewLetters,
  onSelectQuestion,
  selectedQuestionId,
  isViewOnly = false,
  showChartLink = false,
}) => {
  const selectedExerciseId = useExerciseStore((s) => s.currentSelectedExercise);
  const storedCompletedQuestions = useMemo(() => {
    const history = useQuestionsStore.getState().getQuestionHistory(selectedExerciseId);
    return history?.completed || [];
  }, [selectedExerciseId]);
  
  const { t } = useTranslation();
  const [openChartModal, setOpenChartModal] = React.useState(false);
  const [chartImage, setChartImage] = React.useState<string | null>(null);

  // Find the first question with chart_image_data
  const firstChartQuestion = exerciseQuestions.find(q => q.chart_image_data);

  const handleOpenChart = () => {
    if (firstChartQuestion?.chart_image_data) {
      setChartImage(firstChartQuestion.chart_image_data);
      setOpenChartModal(true);
    }
  };
  const handleCloseChart = () => setOpenChartModal(false);

  return (
    <div className="practice-questions-card-container exercise-question-list">
      {isViewOnly ? "" : <div><h4 className="steps-card-title exercise-question-title">{t("selectSectionToStartFrom")}</h4></div>}
      {exerciseQuestions.length === 0 && (
        <div className="loaderBlock below-header">
          {t("thinking")}...
          <img src={Loader} alt="Loading..." />
        </div>
      )}
      <div className="questions-accordion-list exercise-question-items">
        {exerciseQuestions.map((exerciseQuestion, index) => (
          <div
            key={exerciseQuestion.id}
            className={`step-accordion-item question-item-card ${
              selectedQuestionId === exerciseQuestion.id ? "active" : ""
            } ${exerciseQuestion.isInProcess && !exerciseQuestion.isCompleted ? "in-process" : ""}`}
            onClick={() =>
              !isViewOnly &&
              onSelectQuestion &&
              onSelectQuestion(exerciseQuestion, hebrewLetters[index])
            }
          >
            {(exerciseQuestion.isCompleted || storedCompletedQuestions.includes(exerciseQuestion.id) || (exerciseQuestion.isInProcess && !exerciseQuestion.isCompleted)) && (
              <div className="question-status-pills">
                {(exerciseQuestion.isCompleted || storedCompletedQuestions.includes(exerciseQuestion.id)) && (
                  <div className="status-pill completed-pill">{t("completed")}</div>
                )}
                {exerciseQuestion.isInProcess && !exerciseQuestion.isCompleted && (
                  <div className="status-pill in-process-pill">{t("inProgress")}</div>
                )}
              </div>
            )}
            <div className="step-accordion-header">
              <span className="step-number-tag">{hebrewLetters[index] || index + 1}</span>
              {/* If there was a title we'd put it in step-title-text, but we only have question text */}
              {exerciseQuestion.chart_image_data && showChartLink && (
                <Link
                  className="chart-link"
                  component="button"
                  onClick={e => {
                    e.stopPropagation();
                    setChartImage(exerciseQuestion.chart_image_data!);
                    setOpenChartModal(true);
                  }}
                >
                  סירטוט
                </Link>
              )}
            </div>
            <div className="step-accordion-details">
              <MathExpressionNew content={exerciseQuestion.question} />
            </div>
          </div>
        ))}
        <Modal open={openChartModal} onClose={handleCloseChart} className="chart-modal-root">
          <Box className="chart-modal-box">
            <button
              className="chart-modal-close"
              onClick={handleCloseChart}
              aria-label="Close"
            >
              ×
            </button>
            <img
              src={chartImage ? (chartImage.startsWith('data:') ? chartImage : `data:image/png;base64,${chartImage}`) : ''}
              alt="Chart"
              className="chart-modal-img"
            />
          </Box>
        </Modal>
      </div>
    </div>
  );
};

export default ExerciseQuestionList;